import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { sk } from "date-fns/locale";
import { toast } from "sonner";
import { CalendarDays, Clock, Check, ArrowLeft, XCircle, RefreshCw, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Calendar } from "@/components/ui/calendar";
import { api } from "@/lib/api";

const SLOTS = [
  { key: "morning", label: "Doobeda", time: "8:00 – 12:00" },
  { key: "afternoon", label: "Poobede", time: "12:00 – 16:00" },
  { key: "evening", label: "Podvečer", time: "16:00 – 19:00" },
];

const STATUS_LABEL = { new: "Nová", confirmed: "Potvrdená", done: "Vybavená", cancelled: "Zrušená" };

export default function ManageBooking() {
  const [params] = useSearchParams();
  const bid = params.get("b") || "";
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("view"); // view | reschedule | cancelled | rescheduled
  const [blockedDates, setBlockedDates] = useState([]);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");

  const loadBooking = useCallback(async () => {
    if (!bid) { setNotFound(true); setLoading(false); return; }
    try {
      const { data } = await api.get(`/bookings/${bid}`);
      setBooking(data);
      if (data.status === "cancelled") setMode("cancelled");
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [bid]);

  useEffect(() => {
    document.title = "Správa rezervácie — Zateplenia.eu";
    window.scrollTo(0, 0);
    loadBooking();
    api.get("/booking/blocked-dates").then((r) => setBlockedDates(r.data?.dates || [])).catch(() => {});
  }, [loadBooking]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const blockedDateObjs = blockedDates.map((d) => { const [y, m, day] = d.split("-").map(Number); return new Date(y, m - 1, day); });

  const doCancel = async () => {
    if (!window.confirm("Naozaj chcete zrušiť tento termín obhliadky?")) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${bid}/cancel`);
      setMode("cancelled");
      setBooking((b) => ({ ...b, status: "cancelled" }));
      window.scrollTo(0, 0);
    } catch {
      toast.error("Zrušenie sa nepodarilo. Skúste to znova.");
    } finally { setBusy(false); }
  };

  const doReschedule = async () => {
    if (!date || !slot) { toast.error("Vyberte nový dátum a čas."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bid}/reschedule`, { date: format(date, "yyyy-MM-dd"), time_slot: slot });
      setBooking((b) => ({ ...b, date: data.date, slot_label: data.slot_label, status: "new" }));
      setMode("rescheduled");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zmena termínu sa nepodarila.");
    } finally { setBusy(false); }
  };

  const fmtDate = (d) => { try { return format(new Date(d), "d. MMMM yyyy", { locale: sk }); } catch { return d; } };

  return (
    <div className="bg-ink min-h-screen">
      <Header />
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-32 pb-24" data-testid="manage-booking-page">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-gold text-sm mb-8 font-mono2 uppercase tracking-widest" data-testid="manage-booking-back">
          <ArrowLeft className="w-4 h-4" /> Späť na hlavnú
        </Link>

        {loading ? (
          <div className="flex items-center gap-3 text-white/60" data-testid="manage-booking-loading"><Loader2 className="w-5 h-5 animate-spin text-gold" /> Načítavam rezerváciu…</div>
        ) : notFound ? (
          <div className="max-w-xl border border-white/10 bg-ink-2 p-10" data-testid="manage-booking-notfound">
            <h1 className="font-display text-4xl text-white">Rezervácia sa nenašla</h1>
            <p className="text-white/60 mt-4">Odkaz je neplatný alebo rezervácia už neexistuje. Novú obhliadku si môžete rezervovať kedykoľvek.</p>
            <Link to="/rezervacia" className="inline-block mt-8 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors" data-testid="manage-booking-new">
              Rezervovať obhliadku
            </Link>
          </div>
        ) : mode === "cancelled" ? (
          <div className="max-w-xl border border-white/10 bg-ink-2 p-10 text-center" data-testid="manage-booking-cancelled">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-white/60" />
            </div>
            <h1 className="font-display text-4xl text-white">Termín je zrušený</h1>
            <p className="text-white/60 mt-4 leading-relaxed">Vaša rezervácia obhliadky bola zrušená. Ak si to rozmyslíte, radi vám nájdeme nový termín.</p>
            <Link to="/rezervacia" className="inline-block mt-8 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors" data-testid="manage-booking-rebook">
              Rezervovať nový termín
            </Link>
          </div>
        ) : mode === "rescheduled" ? (
          <div className="max-w-xl border border-gold/30 bg-ink-2 p-10 text-center" data-testid="manage-booking-rescheduled">
            <div className="w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-display text-4xl text-white">Termín zmenený</h1>
            <p className="text-white/60 mt-4 leading-relaxed">
              Nový termín obhliadky: <span className="text-gold font-semibold">{fmtDate(booking.date)}</span> · {booking.slot_label}.
              Potvrdenie sme poslali na váš e-mail.
            </p>
            <Link to="/" className="inline-block mt-8 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors" data-testid="manage-booking-home">
              Späť na hlavnú stránku
            </Link>
          </div>
        ) : (
          <>
            <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Správa rezervácie</p>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.02] text-white">
              Vaša obhliadka<br /><span className="text-gold">{fmtDate(booking.date)}</span>
            </h1>

            {/* current details */}
            <div className="mt-10 border border-white/10 bg-ink-2 p-6 max-w-xl" data-testid="manage-booking-details">
              <div className="flex items-center gap-2 text-white font-semibold"><CalendarDays className="w-5 h-5 text-gold" /> {fmtDate(booking.date)}</div>
              <div className="flex items-center gap-2 text-white/60 text-sm mt-2"><Clock className="w-4 h-4 text-gold" /> {booking.slot_label}</div>
              <div className="text-white/40 text-xs mt-3">Stav: {STATUS_LABEL[booking.status] || booking.status} · {booking.name}</div>
            </div>

            {mode === "view" && (
              <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
                <button onClick={() => setMode("reschedule")} data-testid="manage-booking-reschedule-btn" className="flex-1 inline-flex items-center justify-center gap-2 border border-gold text-gold py-3 font-bold uppercase tracking-wider text-sm hover:bg-gold hover:text-ink transition-colors">
                  <RefreshCw className="w-4 h-4" /> Presunúť termín
                </button>
                <button onClick={doCancel} disabled={busy} data-testid="manage-booking-cancel-btn" className="flex-1 inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 py-3 font-bold uppercase tracking-wider text-sm hover:border-red-400 hover:text-red-400 transition-colors disabled:opacity-50">
                  <XCircle className="w-4 h-4" /> Zrušiť termín
                </button>
              </div>
            )}

            {mode === "reschedule" && (
              <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="manage-booking-reschedule-panel">
                <div className="space-y-8">
                  <div className="border border-white/10 bg-ink-2 p-6">
                    <div className="flex items-center gap-2 text-white font-semibold mb-4"><CalendarDays className="w-5 h-5 text-gold" /> Nový dátum</div>
                    <div className="flex justify-center" data-testid="manage-booking-calendar">
                      <Calendar
                        mode="single"
                        selected={date}
                        defaultMonth={date || (booking?.date ? new Date(booking.date) : undefined)}
                        onSelect={setDate}
                        locale={sk}
                        formatters={{ formatCaption: (d) => { const s = format(d, "LLLL yyyy", { locale: sk }); return s.charAt(0).toUpperCase() + s.slice(1); } }}
                        disabled={[{ before: today }, ...blockedDateObjs]}
                        classNames={{
                          caption_label: "text-sm font-medium text-white capitalize",
                          head_cell: "text-white/40 rounded-md w-9 font-normal text-[0.8rem]",
                          day: "h-9 w-9 p-0 font-normal text-white/80 hover:bg-white/10 rounded-md aria-selected:opacity-100",
                          day_selected: "bg-gold text-ink hover:bg-gold focus:bg-gold font-bold",
                          day_today: "border border-gold/40 text-gold",
                          day_disabled: "text-white/20 opacity-40",
                          nav_button: "h-7 w-7 bg-transparent p-0 text-white/60 hover:text-gold border border-white/10 rounded-md",
                        }}
                      />
                    </div>
                  </div>
                  <div className="border border-white/10 bg-ink-2 p-6">
                    <div className="flex items-center gap-2 text-white font-semibold mb-4"><Clock className="w-5 h-5 text-gold" /> Časový úsek</div>
                    <div className="grid grid-cols-3 gap-3">
                      {SLOTS.map((s) => (
                        <button key={s.key} onClick={() => setSlot(s.key)} data-testid={`manage-slot-${s.key}`} className={`p-4 border text-left transition-colors ${slot === s.key ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/30"}`}>
                          <div className={`font-semibold text-sm ${slot === s.key ? "text-gold" : "text-white"}`}>{s.label}</div>
                          <div className="text-white/40 text-xs mt-1 font-mono2">{s.time}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="border border-white/10 bg-ink-2 p-6 h-fit">
                  <div className="text-white font-semibold mb-4">Potvrdiť zmenu termínu</div>
                  {date && slot ? (
                    <p className="text-sm text-white/60 mb-6">Nový termín: <span className="text-gold font-semibold">{format(date, "d. MMMM yyyy", { locale: sk })}</span> · {SLOTS.find((s) => s.key === slot)?.time}</p>
                  ) : (
                    <p className="text-sm text-white/40 mb-6">Vyberte dátum a čas.</p>
                  )}
                  <button onClick={doReschedule} disabled={busy || !date || !slot} data-testid="manage-booking-reschedule-confirm" className={`w-full py-4 font-bold uppercase tracking-wider text-sm transition-colors flex items-center justify-center gap-2 ${busy || !date || !slot ? "bg-white/10 text-white/40 cursor-not-allowed" : "bg-gold text-ink hover:bg-gold-light"}`}>
                    {busy ? "Ukladám…" : "Potvrdiť nový termín"}
                  </button>
                  <button onClick={() => setMode("view")} data-testid="manage-booking-reschedule-back" className="w-full mt-3 text-white/40 hover:text-white text-xs uppercase tracking-widest">
                    Späť
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
